/**
 * UIPanel — Base class for all HTML/CSS game panels.
 * All panels extend this class and are appended inside #ui-overlay.
 *
 * @module UIPanel
 */

import DebugLog from '../debug/DebugLog.js';

/**
 * Base panel class providing show/hide/toggle, focus management,
 * and keyboard/gamepad navigation.
 *
 * @example
 * class InventoryPanel extends UIPanel {
 *   constructor() {
 *     super('panel-inventory', { trapFocus: false });
 *   }
 *   _buildHTML() { return '<div>...</div>'; }
 * }
 */
export class UIPanel {
  /**
   * @param {string} id — DOM element id
   * @param {object} [options={}]
   * @param {Function} [options.onOpen] — Called with data when panel opens
   * @param {Function} [options.onClose] — Called when panel closes
   * @param {boolean} [options.trapFocus=false] — If true, Tab cannot leave the panel (modal)
   * @param {string} [options.zLayer='panel'] — 'hud'|'panel'|'modal'|'toast'
   */
  constructor(id, options = {}) {
    this.id = id;
    this._visible = false;
    this._onOpen  = options.onOpen  ?? null;
    this._onClose = options.onClose ?? null;
    this._trapFocus = options.trapFocus ?? false;
    this._zLayer    = options.zLayer ?? 'panel';

    this._focusableSelector = [
      'button:not([disabled])',
      '[tabindex="0"]:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'a[href]',
    ].join(', ');

    this._focusedIndex   = 0;
    this._focusableEls   = [];
    this._prevFocus      = null;

    // Resolve or create the DOM element
    this.el = document.getElementById(id);
    if (!this.el) {
      this.el = this._create(id);
    }

    this._applyBaseClasses();
    this._bindPanelKeys();
  }

  // ─── DOM setup ───────────────────────────────────────────────────────────

  /**
   * Create a new panel DOM element and append it to #ui-overlay.
   * Subclasses can override _buildHTML() to provide initial content.
   * @param {string} id
   * @returns {HTMLElement}
   */
  _create(id) {
    const el = document.createElement('div');
    el.id = id;
    el.className = 'spellroads-panel';

    const html = this._buildHTML?.();
    if (html) el.innerHTML = html;

    const overlay = document.getElementById('ui-overlay');
    if (overlay) {
      overlay.appendChild(el);
    } else {
      document.body.appendChild(el);
      DebugLog.warn(`[ui] UIPanel "${id}": #ui-overlay not found, appended to body`);
    }

    return el;
  }

  /**
   * Optionally overridden by subclasses to provide initial innerHTML.
   * @returns {string|null}
   */
  _buildHTML() {
    return null;
  }

  _applyBaseClasses() {
    this.el.classList.add('spellroads-panel');

    // CSS custom properties (var(...)) cannot be assigned via element.style.zIndex —
    // they only work through setProperty on the CSSStyleDeclaration.
    const zVarMap = {
      hud:   'var(--z-hud)',
      panel: 'var(--z-panel)',
      modal: 'var(--z-modal)',
      toast: 'var(--z-toast)',
    };
    const z = zVarMap[this._zLayer] ?? zVarMap.panel;
    this.el.style.setProperty('z-index', z);
  }

  // ─── Visibility ──────────────────────────────────────────────────────────

  /**
   * Show the panel. Adds 'visible' class and manages focus.
   * @param {*} [data] — Passed to onOpen callback and updateContent
   */
  show(data) {
    this.el.classList.add('visible');
    this._visible = true;

    // Remember what was focused before opening
    this._prevFocus = document.activeElement;

    // Refresh focusable list and move focus to first element
    this._refreshFocusables();
    this._focusedIndex = 0;
    if (this._focusableEls.length > 0) {
      this._focusableEls[0].focus();
    }

    if (this._onOpen) this._onOpen(data);
    if (typeof this.updateContent === 'function') this.updateContent(data);

    DebugLog.info(`[ui] Panel opened: ${this.id}`);
  }

  /**
   * Hide the panel. Removes 'visible' class and restores focus.
   */
  hide() {
    this.el.classList.remove('visible');
    this._visible = false;

    // Restore focus to what was focused before the panel opened
    if (this._prevFocus && typeof this._prevFocus.focus === 'function') {
      try { this._prevFocus.focus(); } catch (_) { /* element may be gone */ }
    }
    this._prevFocus = null;

    if (this._onClose) this._onClose();

    DebugLog.info(`[ui] Panel closed: ${this.id}`);
  }

  /**
   * Toggle visibility.
   * @param {*} [data]
   */
  toggle(data) {
    this._visible ? this.hide() : this.show(data);
  }

  /**
   * @returns {boolean}
   */
  isVisible() {
    return this._visible;
  }

  // ─── Content ─────────────────────────────────────────────────────────────

  /**
   * Replace the panel's innerHTML and refresh the focusable element list.
   * @param {string} html
   */
  setContent(html) {
    this.el.innerHTML = html;
    this._refreshFocusables();
  }

  // ─── Focus management ─────────────────────────────────────────────────────

  _refreshFocusables() {
    this._focusableEls = Array.from(
      this.el.querySelectorAll(this._focusableSelector)
    ).filter(el => !el.closest('[aria-hidden="true"]'));
  }

  /**
   * Move focus to the next focusable element within the panel.
   * Wraps around at the end.
   */
  focusNext() {
    this._refreshFocusables();
    if (this._focusableEls.length === 0) return;
    this._focusedIndex = (this._focusedIndex + 1) % this._focusableEls.length;
    this._focusableEls[this._focusedIndex].focus();
  }

  /**
   * Move focus to the previous focusable element within the panel.
   * Wraps around at the start.
   */
  focusPrev() {
    this._refreshFocusables();
    if (this._focusableEls.length === 0) return;
    this._focusedIndex =
      (this._focusedIndex - 1 + this._focusableEls.length) % this._focusableEls.length;
    this._focusableEls[this._focusedIndex].focus();
  }

  /**
   * Programmatically activate (click) the currently focused element.
   */
  confirmFocused() {
    this._refreshFocusables();
    const el = this._focusableEls[this._focusedIndex];
    if (el) {
      el.click();
    }
  }

  /**
   * Move focus to element at a specific index.
   * @param {number} index
   */
  focusAt(index) {
    this._refreshFocusables();
    if (this._focusableEls.length === 0) return;
    this._focusedIndex = Math.max(0, Math.min(index, this._focusableEls.length - 1));
    this._focusableEls[this._focusedIndex].focus();
  }

  // ─── Keyboard navigation ─────────────────────────────────────────────────

  _bindPanelKeys() {
    this.el.addEventListener('keydown', (e) => {
      if (!this._visible) return;

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          this.focusNext();
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          this.focusPrev();
          break;
        case 'Enter':
          // Natural; let the element handle it
          break;
        case 'Escape':
          this.hide();
          break;
        case 'Tab':
          if (this._trapFocus) {
            e.preventDefault();
            if (e.shiftKey) {
              this.focusPrev();
            } else {
              this.focusNext();
            }
          }
          break;
        default:
          break;
      }
    });
  }

  // ─── Utility ─────────────────────────────────────────────────────────────

  /**
   * Set a CSS variable on the panel element.
   * @param {string} prop — e.g. '--panel-width'
   * @param {string} value
   */
  setCSSVar(prop, value) {
    this.el.style.setProperty(prop, value);
  }

  /**
   * Destroy the panel — remove its DOM element.
   */
  destroy() {
    if (this._visible) this.hide();
    this.el.remove();
    DebugLog.info(`[ui] Panel destroyed: ${this.id}`);
  }
}

export default UIPanel;
